import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/contexts/ThemeContext';
import { GlobalBackground } from './GlobalBackground';

const DEFAULT_TIMEOUT_MS = 60_000;

function IdleClock({ isLight }: { isLight: boolean }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const h = now.getHours();
  const displayH = h > 12 ? h - 12 : h || 12;
  const m = now.getMinutes().toString().padStart(2, '0');
  const DAYS   = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const dateStr = `${DAYS[now.getDay()]}, ${MONTHS[now.getMonth()]} ${now.getDate()}`;

  return (
    <div className="text-center select-none">
      <p
        style={{
          fontSize:      'clamp(5.5rem, 14vw, 10rem)',
          fontWeight:    300,
          letterSpacing: '-0.04em',
          color:         isLight ? 'rgba(0,0,0,0.82)' : 'rgba(255,255,255,0.90)',
          lineHeight:    1,
          fontFamily:    "'Inter', system-ui, -apple-system, sans-serif",
        }}
      >
        {`${displayH}:${m}`}
      </p>
      <p
        style={{
          fontSize:      '1.1rem',
          fontWeight:    300,
          color:         isLight ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.38)',
          letterSpacing: '0.015em',
          marginTop:     '0.75rem',
          fontFamily:    "'Inter', system-ui, -apple-system, sans-serif",
        }}
      >
        {dateStr}
      </p>
    </div>
  );
}

interface IdleOverlayProps {
  timeoutMs?: number;
  disabled?:  boolean;
}

export function IdleOverlay({ timeoutMs = DEFAULT_TIMEOUT_MS, disabled = false }: IdleOverlayProps) {
  const { resolved } = useTheme();
  const isLight = resolved === 'light';
  const [idle, setIdle] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    setIdle(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (disabled) return;
    timerRef.current = setTimeout(() => setIdle(true), timeoutMs);
  }, [timeoutMs, disabled]);

  useEffect(() => {
    reset();
    window.addEventListener('mousemove',   reset);
    window.addEventListener('keydown',     reset);
    window.addEventListener('touchstart',  reset, { passive: true });
    window.addEventListener('scroll',      reset, { passive: true });
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('mousemove',  reset);
      window.removeEventListener('keydown',    reset);
      window.removeEventListener('touchstart', reset);
      window.removeEventListener('scroll',     reset);
    };
  }, [reset]);

  useEffect(() => {
    if (disabled && idle) setIdle(false);
  }, [disabled, idle]);

  return (
    <AnimatePresence>
      {idle && (
        <motion.div
          key="idle-overlay"
          className="fixed inset-0 flex items-center justify-center cursor-pointer overflow-hidden"
          style={{ zIndex: 9999, background: 'var(--bg-surface)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          onClick={reset}
        >
          <GlobalBackground density="normal" speed="normal" />

          <motion.div
            className="relative z-10 flex flex-col items-center justify-center pointer-events-none"
            initial={{ opacity: 0, scale: 0.97, filter: 'blur(14px)' }}
            animate={{ opacity: 1, scale: 1,    filter: 'blur(0px)' }}
            exit={{    opacity: 0, scale: 0.97, filter: 'blur(14px)' }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <IdleClock isLight={isLight} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
